'use client';
import { Formik, Form } from 'formik';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import { useState } from 'react';
import LoadingLogo from '@components/Loading/LoadingLogo';
import PreferenceCheckbox from './PreferenceCheckbox';
import { useEmailSignup } from '@repo/ui/src/hooks/customer/useEmailSignup';
import { useCustomer } from '@hooks/customer';
import isEqual from 'lodash/isEqual';
import dynamic from 'next/dynamic';
import SuccessCheckIcon from '@repo/ui/src/components/Icons/SucessCheckIcon';
import { useCustomerPreferences } from '@hooks/customer/useCustomerPreferences';
const Markdown = dynamic(() => import('@repo/ui/src/components/Markdown'));

interface Preference {
  description: string;
  title: string;
  label: string;
  disclaimer?: string;
}

interface Props {
  title?: string;
  description?: string;
  formSuccessText?: string;
  emailPreference: Preference;
  smsPreference: Preference;
  saveButtonText: string;
  cancelButtonText: string;
  editButtonText: string;
}

const CommunicationPreferences = ({
  title,
  description,
  emailPreference,
  smsPreference,
  saveButtonText,
  cancelButtonText,
  editButtonText,
  formSuccessText,
}: Props) => {
  const { data: preferences, mutate: mutatePreferences, isLoading } = useCustomerPreferences();
  const { data: user } = useCustomer();
  const [isEditing, setIsEditing] = useState(false);
  const { trigger, data } = useEmailSignup();
  const showSuccess = data?.trackMarketingSignupCompleted.success;

  const handleSubmit = async (values: any, actions: any) => {
    if (!isEqual(values, preferences)) {
      const smsSubscribe =
        values.smsSubscribed !== preferences?.smsSubscribed ? values.smsSubscribed : undefined;
      const emailSubscribe =
        values.emailSubscribed !== preferences?.emailSubscribed
          ? values.emailSubscribed
          : undefined;
      await trigger({
        email: user?.email,
        phoneNumber: user?.phone,
        smsSubscribe,
        sourceDetail: 'profile',
        emailSubscribe,
      });
      mutatePreferences(
        (currentUser) => ({
          ...currentUser!,
          smsSubscribed: smsSubscribe,
          emailSubscribed: emailSubscribe,
        }),
        {
          revalidate: false,
        },
      );
    }
    setIsEditing(false);
    actions.resetForm({ values });
  };

  return (
    <div className="pt-20">
      <h4 className="tra-text-web-heading-4">{title}</h4>
      <hr className="border-2 w-12 border-brand-300 mt-4" />
      <p className="tra-text-web-body-l py-10">{description}</p>
      {formSuccessText && showSuccess && !isEditing && (
        <div className="flex flex-row w-full items-center bg-green-100 border-2 border-green-500 py-5 px-6 mb-5">
          <SuccessCheckIcon className="mr-2" />
          <Markdown className="mb-0">{formSuccessText}</Markdown>
        </div>
      )}
      <div className="bg-warm-100 border border-warm-300 px-4 py-5 sm:p-10">
        {isLoading ? (
          <LoadingLogo className="py-20" />
        ) : (
          <Formik
            validateOnChange={false}
            onSubmit={handleSubmit}
            initialValues={{
              emailSubscribed: preferences?.emailSubscribed,
              smsSubscribed: preferences?.smsSubscribed,
            }}
          >
            {({ isSubmitting, dirty, handleReset }) => (
              <Form>
                <div id="checkbox-group">
                  <div role="group" aria-labelledby="checkbox-group" className="grid gap-10">
                    {emailPreference?.label && (
                      <PreferenceCheckbox
                        title={emailPreference?.title}
                        description={emailPreference?.description}
                        label={emailPreference.label}
                        disclaimer={emailPreference?.disclaimer}
                        name="emailSubscribed"
                        disabled={!isEditing}
                      />
                    )}

                    {smsPreference?.label && (
                      <PreferenceCheckbox
                        title={smsPreference?.title}
                        description={smsPreference?.description}
                        label={smsPreference?.label}
                        disclaimer={smsPreference?.disclaimer}
                        name="smsSubscribed"
                        disabled={!isEditing}
                      />
                    )}
                  </div>
                  <div className="flex flex-col sm:flex-row justify-start pt-5 gap-6">
                    {!isEditing ? (
                      <div>
                        <Button
                          variant={ButtonVariant.secondary}
                          onClick={() => setIsEditing(true)}
                        >
                          {editButtonText}
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Button
                          type="submit"
                          variant={ButtonVariant.secondary}
                          disabled={isSubmitting || !dirty}
                          className="w-full sm:w-fit"
                        >
                          {isSubmitting ? <LoadingLogo small /> : `${saveButtonText}`}
                        </Button>
                        <div>
                          <Button
                            variant={ButtonVariant.secondary}
                            onClick={() => {
                              handleReset();
                              setIsEditing(false);
                            }}
                            disabled={isSubmitting}
                            className="w-full sm:w-fit"
                          >
                            {cancelButtonText}
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </div>
    </div>
  );
};

export default CommunicationPreferences;
